import { FlaskConical, AlertTriangle } from "lucide-react";
import { UnifiedRecord, SystemMetrics } from "../../../types";
import { LabTATChart } from "../../Dashboard/LabTATChart";

interface DashboardProps {
  records: UnifiedRecord[];
  metrics: SystemMetrics | null;
  loading: boolean;
}

export function LabDashboard({ records }: DashboardProps) {
  const orphanedRecords = records.filter(r => r.is_orphaned_lab_record);
  
  // Calculate dynamic Lab TAT trend from records
  const computeChartData = () => {
    const labs = records.flatMap(r => r.lab_results);
    if (labs.length === 0) return undefined;
    const hourly: Record<string, number[]> = {};
    labs.forEach(lab => {
      if (!lab.result_datetime) return;
      const date = new Date(lab.result_datetime);
      if (isNaN(date.getTime())) return;
      const hourStr = date.getHours().toString().padStart(2, "0") + ":00";
      if (!hourly[hourStr]) hourly[hourStr] = [];
      hourly[hourStr].push(lab.tat_minutes);
    });
    const hours = Object.keys(hourly).sort();
    if (hours.length === 0) return undefined;
    return hours.map(time => {
      const tats = hourly[time].sort((a, b) => a - b);
      const avg_tat = Math.round(tats.reduce((sum, val) => sum + val, 0) / tats.length);
      const p90Idx = Math.floor(tats.length * 0.9);
      const p90_tat = Math.round(tats[p90Idx]);
      return { time, avg_tat, p90_tat };
    });
  };

  const chartData = computeChartData();

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3 mb-4 border-b border-white/10 pb-4">
        <FlaskConical className="w-8 h-8 text-purple-400" />
        <div>
          <h2 className="text-xl font-semibold text-white">Laboratory Dashboard</h2>
          <p className="text-gray-400 text-sm">Turnaround times and orphaned results management</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <LabTATChart data={chartData} />
        
        <div className="rounded-2xl border border-purple-500/30 bg-purple-500/5 p-5">
          <h3 className="text-white font-medium flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-purple-400" />
            Orphaned Lab Results ({orphanedRecords.length})
          </h3>
          <div className="space-y-3">
            {orphanedRecords.length === 0 ? (
              <p className="text-gray-500 text-sm">No orphaned results detected.</p>
            ) : (
              orphanedRecords.map((r, i) => (
                <div key={i} className="p-3 bg-black/20 rounded-lg border border-white/5 flex justify-between items-center">
                  <div>
                    <div className="text-purple-300 font-mono text-sm">{r.patient_id}</div>
                    <div className="text-xs text-gray-500">{r.lab_results.length} tests completed</div>
                  </div>
                  <button className="text-xs bg-purple-500/20 text-purple-300 px-3 py-1.5 rounded-md hover:bg-purple-500/30">
                    Map to HIS
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
